import { useRef, Fragment, useState, useEffect, useCallback } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { Conversation, ConversationMessage } from "../../types";
import { Answer, ErrorAnswer } from "../../components/Answer/Answer";
import {
  QuestionInput,
  QuestionInputHandle,
} from "../../components/QuestionInput/QuestionInput";
import { useAppState } from "../../state/App";
import {
  Button,
  ClickAwayListener,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  Tooltip,
} from "@mui/material";
import { useConversation } from "../../hooks/useConversation";
import {
  EventStreamContentType,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { useQueryClient } from "react-query";
import { useSnackbar } from "@myplant-io/snackbar";

import { Question } from "../../components/Question/Question";
import { Splash } from "./Splash";
import { defineMessages, useIntl } from "react-intl";
import { ChatHistoryPanel } from "../../components/ChatHistory/ChatHistoryPanel";
import { ModeMenu } from "../../components/common/ModeMenu";
import { commonMessages } from "../../commonMessages";
import {
  FilesDropzone,
  useFileUploadProps,
} from "../../components/common/FilesSelector";
import { useIsKiosk } from "../../hooks/useIsKiosk";
import { useModes } from "../../hooks/useModes";

const drawerWidth = 300;

type GenerateRequest = {
  mode_id?: string;
  message?: string;
  session_id?: string;
  files?: File[];
  recording?: Blob;
};

type GenerationStarted = {
  conversation_id: string;
  message_id: string;
};

type ChatResponseDocument = {
  document_id: string;
  name: string;
  content: string;
  has_source_link: boolean;
};

type ChatResponse = {
  session_id: string;
  type:
    | "context"
    | "start"
    | "streaming"
    | "end"
    | "error"
    | "transcript"
    | "reasoning"
    | "title_updated";
  value?: string;
  function_name?: string;
  function_value?: string;
  calling_function?: boolean;
  sources?: ChatResponseDocument[];
  message_id?: string;
  standalone_question?: string;
  no_innio_context?: boolean;
  mode_id?: string;
  mode_name?: string;
  error_code?: number;
  audio_url?: string;
  reasoning_value?: string;
};

const intlMessages = defineMessages({
  chatError: {
    id: "chat.error",
    defaultMessage: "An unrecoverable error occurred. Please try again.",
  },
  scrollToBottom: {
    id: "chat.scrollToBottom",
    defaultMessage: "Scroll to Bottom",
  },
});

const generate = async (
  payload: GenerateRequest,
  abortSignal: AbortSignal
): Promise<GenerationStarted> => {
  const formData = new FormData();
  if (payload.mode_id) {
    formData.append("mode_id", payload.mode_id);
  }
  if (payload.message) {
    formData.append("message", payload.message);
  }
  formData.append("session_id", payload.session_id ?? "");
  if (payload.files) {
    payload.files.forEach((file) => {
      formData.append("files", file);
    });
  }
  if (payload.recording) {
    formData.append(
      "recording",
      new File([payload.recording], "recording.wav")
    );
  }
  const response = await fetch("/api/generate/chat_form_sse", {
    method: "POST",
    body: formData,
    signal: abortSignal,
  });
  if (!response.ok) {
    throw response;
  }
  return response.json();
};

const initialResponse = {
  content: "",
  function_content: "",
  conversation_id: "",
  created: "",
  rating: -2,
  role: "ai" as const,
  sources: [],
  updated: "",
  _id: "-1",
};

const checkScrolledBottom = (container: HTMLElement) => {
  return container.scrollHeight - container.scrollTop <= container.clientHeight;
};

const SCROLL_UP_THRESHOLD = 10;

export default function Chat() {
  const isKiosk = useIsKiosk();
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const [isScrolledBottom, setIsScrolledBottom] = useState(true);
  const { formatMessage: t } = useIntl();
  const { showErrorMessage } = useSnackbar();
  const { data: modes } = useModes();
  const {
    state: {
      isChatHistoryOpen,
      currentConversationId,
      isGenerating,
      messages,
      modeId: selectedModeId,
      conversationToStop,
    },
    actions: {
      setChatHistory,
      setIsGenerating,
      setCurrentConversationId,
      addMessages,
      setMessages,
      updateLastMessage,
      setCurrentTTSUrl,
      setIsPlaying,
      setConversationToStop,
    },
  } = useAppState();

  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const isScrolledBottomRef = useRef(true);
  const isGeneratingRef = useRef(isGenerating);
  const lastScrollTopRef = useRef(0);
  const questionInputRef = useRef<QuestionInputHandle | null>(null);
  // Tracks the messages value from the PREVIOUS render. This is needed because
  // newChat() batches setCurrentConversationId + setMessages([]) into one render,
  // so by the time the navigation effect runs, messages is already []. We capture
  // messages BEFORE the clear by reading this ref in the navigation effect, which
  // runs before the messagesRef-update effect (React runs effects in declaration order).
  const prevMessagesRef = useRef<ConversationMessage[]>([]);
  type GenSession = { convId: string | null; isActive: boolean };
  type BgGen = {
    messages: ConversationMessage[];
    isGenerating: boolean;
    abortControllers: AbortController[];
    genSession: GenSession;
  };
  const bgGenRef = useRef<Map<string, BgGen>>(new Map());
  const activeGenSessionRef = useRef<{
    convId: string | null;
    isActive: boolean;
  } | null>(null);
  const currentConversationIdRef = useRef(currentConversationId);
  // Intentionally NOT initialized to currentConversationId: on first render after
  // a browser reload, prevId must differ from currentConversationId so the
  // navigation effect runs makeResumeConnection to reconnect to any active generation.
  const prevConversationIdRef = useRef<string | undefined>(undefined);
  // Stores the [humanMsg, initialResponse] snapshot when the user navigates away
  // from a new conversation before the "start" SSE event fires (conv ID not yet known).
  const pendingNewConvMessagesRef = useRef<ConversationMessage[] | null>(null);
  // Stores abort controllers for a new conversation's generation before "start" fires.
  const pendingNewConvAbortControllersRef = useRef<AbortController[]>([]);
  // Set to a conversationId when the resume SSE confirmed active before DB data arrived;
  // the useConversation callback uses this to add the placeholder and set isGenerating.
  const pendingResumeRef = useRef<string | null>(null);
  // Holds the most-recently accumulated ConversationMessage from the resume SSE stream
  // while DB data is still loading (pendingResumeRef is set). Applied as the initial
  // AI message once useConversation fires, so the full in-progress answer is visible
  // immediately (including after a browser reload).
  const pendingResumeUpdateRef = useRef<ConversationMessage | null>(null);

  const logRefCallback = useCallback((el: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (el && typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(() => {
        if (isScrolledBottomRef.current) {
          chatMessageStreamEnd.current?.scrollIntoView({
            behavior: isGeneratingRef.current ? "auto" : "smooth",
          });
        }
      });
      observerRef.current.observe(el);
      if (isScrolledBottomRef.current) {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
      }
    }
  }, []);
  const abortFuncs = useRef([] as AbortController[]);
  const queryClient = useQueryClient();
  const [isModeEditing, setIsModeEditing] = useState(false);

  const fileUploadProps = useFileUploadProps({
    isGlobal: !isModeEditing,
  });
  const { dropzoneProps } = fileUploadProps;

  useConversation(currentConversationId, function (data) {
    if (pendingResumeRef.current === currentConversationId) {
      // Resume SSE confirmed active before the DB data arrived — now we have both.
      // The server writes an empty AI placeholder at generation start, so `data`
      // should already end with an AI message. If SSE events arrived before the DB
      // data landed, use the accumulated content to replace the empty placeholder.
      pendingResumeRef.current = null;
      const accumulated = pendingResumeUpdateRef.current;
      pendingResumeUpdateRef.current = null;
      const lastIsAI = data.length > 0 && data[data.length - 1].role === "ai";
      if (accumulated) {
        setMessages(
          lastIsAI
            ? [...data.slice(0, -1), accumulated]
            : [...data, accumulated]
        );
      } else {
        setMessages(lastIsAI ? data : [...data, initialResponse]);
      }
      setIsGenerating(true);
    } else if (
      !isGeneratingRef.current &&
      !bgGenRef.current.has(currentConversationId ?? "")
    ) {
      // Use isGeneratingRef (not isGenerating) to avoid stale-closure overwriting
      // bgGen-restored messages before React has processed setIsGenerating(true).

      // If the resume stream completed before this callback fired, the accumulated
      // content is still in pendingResumeUpdateRef. Use it instead of the DB data
      // (which may still have the empty AI placeholder).
      const accumulated = pendingResumeUpdateRef.current;
      if (
        accumulated &&
        accumulated.conversation_id === currentConversationId
      ) {
        pendingResumeUpdateRef.current = null;
        const lastIsAI = data.length > 0 && data[data.length - 1].role === "ai";
        setMessages(
          lastIsAI
            ? [...data.slice(0, -1), accumulated]
            : [...data, accumulated]
        );
      } else {
        pendingResumeUpdateRef.current = null;
        setMessages(data);
      }
    }
  });

  useEffect(() => {
    const onResize = () => {
      if (!chatContainerRef.current) {
        return;
      }
      const scrolledBottom = checkScrolledBottom(chatContainerRef.current);
      isScrolledBottomRef.current = scrolledBottom;
      setIsScrolledBottom(scrolledBottom);
      lastScrollTopRef.current = chatContainerRef.current.scrollTop;
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  useEffect(() => {
    const prevId = prevConversationIdRef.current;
    prevConversationIdRef.current = currentConversationId;

    if (prevId === currentConversationId) return;

    // Clear any stale pending-resume flags from the previous conversation.
    pendingResumeRef.current = null;
    pendingResumeUpdateRef.current = null;

    // Navigating AWAY from an active generation
    if (
      prevId &&
      isGeneratingRef.current &&
      activeGenSessionRef.current?.convId === prevId
    ) {
      // Existing conversation with known ID.
      // Use prevMessagesRef (not the messages closure) because newChat() batches
      // setCurrentConversationId + setMessages([]) — by the time this effect runs,
      // messages is already [] but prevMessagesRef still holds the pre-clear snapshot.
      activeGenSessionRef.current.isActive = false;
      bgGenRef.current.set(prevId, {
        messages: [...prevMessagesRef.current],
        isGenerating: true,
        abortControllers: [...abortFuncs.current],
        genSession: activeGenSessionRef.current,
      });
      isGeneratingRef.current = false;
      setIsGenerating(false);
      // Remove background controllers from abortFuncs so stopGenerating only
      // affects the foreground conversation and never cancels background ones.
      abortFuncs.current = [];
    } else if (
      !prevId &&
      isGeneratingRef.current &&
      activeGenSessionRef.current?.isActive &&
      activeGenSessionRef.current?.convId === null
    ) {
      // New conversation navigated away from before "start" event fired.
      // Save the [humanMsg, initialResponse] snapshot and abort controllers so
      // the "start" handler can store them in bgGenRef once the conv ID is known.
      pendingNewConvMessagesRef.current = [...prevMessagesRef.current];
      pendingNewConvAbortControllersRef.current = [...abortFuncs.current];
      activeGenSessionRef.current.isActive = false;
      isGeneratingRef.current = false;
      setIsGenerating(false);
      abortFuncs.current = [];
    }

    if (currentConversationId) {
      if (bgGenRef.current.has(currentConversationId)) {
        // Navigating BACK to an in-memory background generation (same session)
        const bgGen = bgGenRef.current.get(currentConversationId)!;
        bgGenRef.current.delete(currentConversationId);
        setMessages(bgGen.messages);
        if (bgGen.isGenerating) {
          // Re-activate the original genSession so its routeUpdate routes
          // tokens to the foreground (updateLastMessage) instead of bgGenRef.
          bgGen.genSession.isActive = true;
          activeGenSessionRef.current = bgGen.genSession;
          // Restore abort controllers so stopGenerating works again for this conv.
          abortFuncs.current = bgGen.abortControllers;
          // Sync update so useConversation's onSuccess sees generating=true
          // before React processes setIsGenerating, preventing DB data from
          // overwriting the restored bgGen messages (which have the live AI answer).
          isGeneratingRef.current = true;
          setIsGenerating(true);
        }
      } else {
        // Try to resume a server-side generation (cross-session / multi-replica).
        // Skip if we're already the active generator for this conversation
        // (e.g. the "start" event just assigned the new session ID to currentConversationId).
        const alreadyGenerating =
          activeGenSessionRef.current?.isActive &&
          activeGenSessionRef.current?.convId === currentConversationId;
        if (!alreadyGenerating) {
          makeResumeConnection(currentConversationId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  useEffect(() => {
    isScrolledBottomRef.current = true;
    setIsScrolledBottom(true);
    lastScrollTopRef.current = 0;
  }, [currentConversationId]);

  // MUST be declared after the navigation effect: React runs effects in declaration
  // order, so the navigation effect reads prevMessagesRef before this updates it.
  useEffect(() => {
    prevMessagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const cancelServerGeneration = (conversationId: string) => {
    // Fire-and-forget: tell the server to stop the background generation task.
    // Failure is non-fatal — the task will finish on its own.
    fetch(`/api/generate/cancel/${conversationId}`, { method: "POST" }).catch(
      () => {}
    );
  };

  useEffect(() => {
    if (!conversationToStop) return;
    setConversationToStop(undefined);

    if (
      conversationToStop === currentConversationId &&
      activeGenSessionRef.current?.isActive
    ) {
      // Foreground: abort via the active abort controllers
      abortFuncs.current.forEach((a) => a.abort());
      abortFuncs.current = [];
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }

    // Background: abort controllers stored in bgGenRef
    const bg = bgGenRef.current.get(conversationToStop);
    if (bg) {
      bg.abortControllers.forEach((a) => a.abort());
      bgGenRef.current.delete(conversationToStop);
    }

    cancelServerGeneration(conversationToStop);
    queryClient.setQueryData<Conversation[] | undefined>(
      "conversation",
      (old) =>
        old?.map((c) =>
          c._id === conversationToStop ? { ...c, is_generating: false } : c
        )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationToStop]);

  const stopGenerating = () => {
    abortFuncs.current.forEach((a) => a.abort());
    setIsGenerating(false);
    if (currentConversationId) {
      cancelServerGeneration(currentConversationId);
      queryClient.setQueryData<Conversation[] | undefined>(
        "conversation",
        (old) =>
          old?.map((c) =>
            c._id === currentConversationId ? { ...c, is_generating: false } : c
          )
      );
    }
  };

  const handleError = (_message?: string) => {
    stopGenerating();
    const message = _message ?? t(intlMessages.chatError);
    showErrorMessage({
      message,
    });
  };

  // ------------------------------------------------------------------
  // Shared SSE event processor — used by both makeGenerateRequest and
  // makeResumeConnection so the onmessage logic lives in one place.
  // `state` is a plain object mutated in-place as tokens arrive.
  // ------------------------------------------------------------------
  type StreamState = {
    transcriptText: string;
    runningText: string;
    runningFunctionText: string;
    runningReasoningText: string;
  };

  const processStreamEvent = (
    msg: ReturnType<typeof JSON.parse>,
    state: StreamState,
    genSession: { convId: string | null; isActive: boolean },
    routeUpdate: (msg: ConversationMessage) => void
  ) => {
    if (msg.type === "error") {
      console.error("onmessage", msg);
      // Only surface the error to the user for the foreground generation; a
      // background generation encountering an error should not interrupt the
      // conversation the user is currently viewing.
      if (genSession.isActive) {
        if (msg.error_code === 429)
          handleError(t(commonMessages.chatRequestTooManyTokens));
        else handleError();
      }
      return;
    }
    if (msg.type === "start") {
      const isNewConversation = genSession.convId === null;
      genSession.convId = msg.session_id;
      if (isNewConversation) {
        if (currentConversationIdRef.current == null) {
          // User is still on the new-chat page — navigate to the newly created conversation.
          setCurrentConversationId(msg.session_id);
        } else {
          // User navigated away before "start" arrived — restore the saved snapshot
          // (human message + initialResponse placeholder) so navigating back shows
          // the full conversation including the in-progress answer.
          const savedMessages = pendingNewConvMessagesRef.current ?? [
            { ...initialResponse },
          ];
          pendingNewConvMessagesRef.current = null;
          const savedAbortControllers =
            pendingNewConvAbortControllersRef.current;
          pendingNewConvAbortControllersRef.current = [];
          // genSession.convId was just set to msg.session_id; isActive was set
          // to false by the navigation effect when the user navigated away.
          bgGenRef.current.set(msg.session_id, {
            messages: savedMessages,
            isGenerating: true,
            abortControllers: savedAbortControllers,
            genSession: activeGenSessionRef.current!,
          });
        }
      }
      // For existing conversations the convId was already set; no navigation needed.
      queryClient.invalidateQueries("conversation");
      return;
    }
    if (msg.type === "title_updated") {
      queryClient.invalidateQueries("conversation");
      return;
    }
    if (msg.audio_url) {
      setCurrentTTSUrl(msg.audio_url);
      setIsPlaying(true);
    }
    const value = msg.value ?? "";
    const function_value = msg.function_value ?? "";
    const calling_function = msg.calling_function ?? false;

    if (msg.type === "reasoning") {
      state.runningReasoningText += msg.reasoning_value ?? "";
    } else if (msg.type === "transcript") {
      state.transcriptText = value;
    } else if (msg.type === "end") {
      state.runningText = value;
      state.runningFunctionText = function_value;
    } else {
      state.runningText += value;
      state.runningFunctionText += function_value;
    }

    if (msg.type === "reasoning") {
      routeUpdate({
        _id: msg.message_id,
        conversation_id: msg.session_id,
        content: state.runningText,
        function_content: state.runningFunctionText,
        reasoning_content: state.runningReasoningText,
        created: new Date().toISOString(),
        rating: 0,
        role: "ai",
        sources: msg.sources,
        updated: new Date().toISOString(),
        no_innio_context: msg.no_innio_context,
        mode_id: msg.mode_id,
        mode_name: msg.mode_name,
      });
      return;
    }

    const isTranscript = msg.type === "transcript";
    routeUpdate({
      _id: msg.message_id,
      conversation_id: msg.session_id,
      content: isTranscript ? state.transcriptText : state.runningText,
      function_name: msg.function_name,
      function_content: state.runningFunctionText,
      reasoning_content: state.runningReasoningText || undefined,
      created: new Date().toISOString(),
      rating: 0,
      role: isTranscript ? "human" : "ai",
      sources: msg.sources,
      updated: new Date().toISOString(),
      calling_function: calling_function,
      standalone_question: msg.standalone_question,
      no_innio_context: msg.no_innio_context,
      mode_id: msg.mode_id,
      mode_name: msg.mode_name,
    });
  };

  // ------------------------------------------------------------------
  // Reconnect to an in-progress server-side generation.
  // Called on every navigation to a conversation; silently aborts if
  // the server returns 404 (no active generation).
  // ------------------------------------------------------------------
  const makeResumeConnection = async (conversationId: string) => {
    const abortController = new AbortController();
    let connectionConfirmed = false;
    const genSession = {
      convId: conversationId as string | null,
      isActive: true,
    };
    const state: StreamState = {
      transcriptText: "",
      runningText: "",
      runningFunctionText: "",
      runningReasoningText: "",
    };

    const routeUpdate = (msg: ConversationMessage) => {
      if (genSession.isActive) {
        if (pendingResumeRef.current) {
          pendingResumeUpdateRef.current = msg;
        } else {
          updateLastMessage(msg);
        }
      } else if (genSession.convId) {
        const bg = bgGenRef.current.get(genSession.convId);
        if (bg) {
          bg.messages = bg.messages.map((m, i) =>
            i === bg.messages.length - 1 ? msg : m
          );
        }
      }
    };

    try {
      await fetchEventSource(`/api/generate/subscribe/${conversationId}`, {
        method: "GET",
        signal: abortController.signal,
        openWhenHidden: true,
        onerror: (e) => {
          throw e;
        },
        async onopen(response) {
          if (
            response.ok &&
            response.headers
              .get("content-type")
              ?.startsWith(EventStreamContentType)
          ) {
            connectionConfirmed = true;
            activeGenSessionRef.current = genSession;
            abortFuncs.current.unshift(abortController);

            const cached = queryClient.getQueryData<ConversationMessage[]>([
              "conversation",
              conversationId,
            ]);
            if (cached && cached.length > 0) {
              const lastIsAI = cached[cached.length - 1].role === "ai";
              setMessages(lastIsAI ? cached : [...cached, initialResponse]);
              setIsGenerating(true);
            } else {
              pendingResumeRef.current = conversationId;
            }
            return;
          }
          abortController.abort();
        },
        onmessage(event) {
          if (!connectionConfirmed) return;
          const msg = JSON.parse(event.data);
          processStreamEvent(msg, state, genSession, routeUpdate);
        },
      });
    } catch {
      // Ignore — resume is best-effort; normal DB load covers non-active convs.
    }

    pendingResumeRef.current = null;

    const ctrlIdx = abortFuncs.current.indexOf(abortController);
    if (ctrlIdx !== -1) abortFuncs.current.splice(ctrlIdx, 1);

    if (connectionConfirmed) {
      if (currentConversationIdRef.current === genSession.convId) {
        setIsGenerating(false);
      } else {
        const bg = bgGenRef.current.get(genSession.convId!);
        if (bg) bg.isGenerating = false;
      }
      queryClient.invalidateQueries("conversation");
    }
  };

  const makeGenerateRequest = async ({
    modeId,
    message,
    files,
    sessionId,
    recording,
  }: {
    modeId?: string;
    message?: string;
    files?: File[];
    sessionId?: string;
    recording?: Blob;
  }) => {
    setIsGenerating(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);
    const effectiveModeId = modeId || selectedModeId;
    const selectedMode = modes?.find((m) => m._id === effectiveModeId);

    addMessages([
      {
        role: "human",
        content: message ?? "...",
        function_content: "",
        conversation_id: "",
        created: "",
        rating: 0,
        sources: [],
        updated: "",
        _id: "",
        attachments: files?.map(({ name }) => ({
          _id: "",
          mime_type: "",
          file_name: name,
        })),
        recording,
      },
      {
        ...initialResponse,
        mode_id: effectiveModeId,
        mode_name: selectedMode?.name,
      },
    ]);

    const genSession = { convId: sessionId ?? null, isActive: true };
    activeGenSessionRef.current = genSession;

    // Step 1: Start generation — POST returns immediately with IDs
    let startResult: GenerationStarted;
    try {
      startResult = await generate(
        {
          mode_id: effectiveModeId,
          message,
          session_id: sessionId,
          files,
          recording,
        },
        abortController.signal
      );
    } catch (e) {
      if (e instanceof Response) {
        if (e.status === 413)
          handleError(t(commonMessages.chatRequestEntityTooLarge));
        else if (e.status === 429)
          handleError(t(commonMessages.chatRequestTooManyTokens));
        else handleError();
      } else if (!abortController.signal.aborted) {
        handleError();
      }
      return;
    }

    genSession.convId = startResult.conversation_id;

    // POST succeeded — clear the input now
    questionInputRef.current?.clearInput();

    // Navigate to new conversation or stash in background
    if (!sessionId) {
      if (currentConversationIdRef.current == null) {
        setCurrentConversationId(startResult.conversation_id);
      } else {
        // User navigated away before POST returned — save to bgGenRef
        const savedMessages = pendingNewConvMessagesRef.current ?? [
          { ...initialResponse },
        ];
        pendingNewConvMessagesRef.current = null;
        const savedAbortControllers = pendingNewConvAbortControllersRef.current;
        pendingNewConvAbortControllersRef.current = [];
        bgGenRef.current.set(startResult.conversation_id, {
          messages: savedMessages,
          isGenerating: true,
          abortControllers: savedAbortControllers,
          genSession: activeGenSessionRef.current!,
        });
      }
    }
    queryClient.invalidateQueries("conversation");

    // Step 2: Subscribe to streaming updates via the resume endpoint
    const state: StreamState = {
      transcriptText: "",
      runningText: "",
      runningFunctionText: "",
      runningReasoningText: "",
    };
    const routeUpdate = (msg: ConversationMessage) => {
      if (genSession.isActive) {
        updateLastMessage(msg);
      } else if (genSession.convId) {
        const bg = bgGenRef.current.get(genSession.convId);
        if (bg) {
          bg.messages = bg.messages.map((m, i) =>
            i === bg.messages.length - 1 ? msg : m
          );
        }
      }
    };

    try {
      await fetchEventSource(
        `/api/generate/subscribe/${startResult.conversation_id}?message_id=${startResult.message_id}`,
        {
          method: "GET",
          signal: abortController.signal,
          openWhenHidden: true,
          onerror: (e) => {
            throw e;
          },
          async onopen(response) {
            if (
              response.ok &&
              response.headers
                .get("content-type")
                ?.startsWith(EventStreamContentType)
            ) {
              return;
            }
            abortController.abort();
          },
          onmessage(event) {
            const msg = JSON.parse(event.data) as ChatResponse;
            processStreamEvent(msg, state, genSession, routeUpdate);
          },
        }
      );
    } catch {
      // Stream ended or was aborted
    }

    const ctrlIdx = abortFuncs.current.indexOf(abortController);
    if (ctrlIdx !== -1) abortFuncs.current.splice(ctrlIdx, 1);

    if (currentConversationIdRef.current === genSession.convId) {
      setIsGenerating(false);
    } else {
      const bg = bgGenRef.current.get(genSession.convId!);
      if (bg) bg.isGenerating = false;
    }
    queryClient.invalidateQueries("conversation");
  };

  const newChat = () => {
    setCurrentConversationId(undefined);
    setMessages([]);
  };

  const scrollToBottomOnNextTick = (
    arg?: boolean | ScrollIntoViewOptions | undefined
  ) => {
    setTimeout(() => {
      chatMessageStreamEnd.current?.scrollIntoView(arg);
    }, 0);
  };

  const disabledButton = () => {
    return messages && messages.length === 0;
  };

  const onScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as EventTarget & HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrolledUp =
      lastScrollTopRef.current - scrollTop > SCROLL_UP_THRESHOLD;
    lastScrollTopRef.current = scrollTop;

    if (scrolledUp) {
      isScrolledBottomRef.current = false;
      setIsScrolledBottom(false);
    } else {
      // Scrolling down (content growth or programmatic) — re-enable if at bottom
      if (!isScrolledBottomRef.current) {
        const scrolledBottom = checkScrolledBottom(target);
        if (scrolledBottom) {
          isScrolledBottomRef.current = true;
          setIsScrolledBottom(true);
        }
      }
    }
  };

  return (
    <FilesDropzone {...dropzoneProps}>
      <div className="flex flex-col flex-1" role="main">
        <div className="z-10 md:absolute pb-0 p-4 sm:px-6">
          <div className="flex -mt-2 -ml-2 px-2 py-2 backdrop-blur-sm">
            {!isKiosk && (
              <ModeMenu onModeSelected={(m) => setIsModeEditing(Boolean(m))} />
            )}
          </div>
        </div>
        <div
          className="full flex flex-col flex-1 overflow-y-auto overflow-x-hidden"
          onScroll={onScroll}
          id="chat-container"
          ref={chatContainerRef}
          style={{
            paddingRight:
              !isKiosk && isLg && isChatHistoryOpen ? drawerWidth : 0,
          }}
        >
          <div className="flex flex-col flex-1 pt-0 p-4 sm:px-6 max-w-5xl m-auto w-full">
            <AutoSizer>
              {({ height, width }: Size) => (
                <div style={{ height, width }}>
                  {!messages || messages.length < 1 ? (
                    <Splash />
                  ) : (
                    <div role="log" ref={logRefCallback}>
                      {messages.map((answer, index) => (
                        <Fragment key={index}>
                          {answer.role === "human" ? (
                            <div className="my-4 flex justify-end" tabIndex={0}>
                              <Question answer={answer} />
                            </div>
                          ) : answer.role === "ai" ? (
                            <div className="my-4 md:max-w-[80%] md:min-w-[80%] flex">
                              <Answer
                                key={`${currentConversationId}|${index}`}
                                answer={answer}
                                isGenerating={
                                  isGenerating && index === messages.length - 1
                                }
                              />
                            </div>
                          ) : answer.role === "error" ? (
                            <div className="my-4 md:max-w-[80%] md:min-w-[80%] flex">
                              <ErrorAnswer answer={answer} />
                            </div>
                          ) : null}
                        </Fragment>
                      ))}
                      <div ref={chatMessageStreamEnd} />
                      <div className="h-32"></div>
                    </div>
                  )}

                  <div
                    className="flex flex-col absolute bottom-0"
                    style={{
                      backgroundColor: theme.palette.background.default,
                      width: width,
                    }}
                  >
                    {!isScrolledBottom && (
                      <div className="absolute right-0 -top-12">
                        <Tooltip title={t(intlMessages.scrollToBottom)}>
                          <IconButton
                            onClick={() => scrollToBottomOnNextTick()}
                            aria-label="scroll to bottom"
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    {isGenerating && (
                      <div
                        className="flex justify-end mb-2"
                        style={{
                          backgroundColor: theme.palette.background.default,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          aria-label="Stop generating"
                          aria-hidden="true"
                          tabIndex={0}
                          onClick={stopGenerating}
                          onKeyDown={(e) =>
                            e.key === "Enter" || e.key === " "
                              ? stopGenerating()
                              : null
                          }
                          startIcon={<CheckBoxOutlineBlankIcon />}
                        >
                          Stop generating
                        </Button>
                      </div>
                    )}

                    <div className="flex items-center mb-4 z-[1]">
                      <div>
                        {messages.length > 0 && (
                          <div className="hidden 2lg:flex mr-2 2lg:-ml-14">
                            <IconButton
                              size="large"
                              onClick={newChat}
                              disabled={disabledButton()}
                              aria-label="start a new chat button"
                            >
                              <AddIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <QuestionInput
                        ref={questionInputRef}
                        fileUploadProps={fileUploadProps}
                        placeholder="Type a new question..."
                        disabled={isGenerating}
                        onSend={({
                          modeId,
                          message: message,
                          files,
                          sessionId: sessionId,
                          recording,
                        }) => {
                          makeGenerateRequest({
                            modeId,
                            message,
                            files,
                            sessionId,
                            recording,
                          });
                        }}
                        sessionId={currentConversationId}
                        loading={isGenerating}
                      />
                    </div>
                  </div>
                </div>
              )}
            </AutoSizer>
          </div>
        </div>

        {!isKiosk && (
          <ClickAwayListener
            onClickAway={() => {
              if (!isLg && isChatHistoryOpen) {
                setChatHistory(false);
              }
            }}
          >
            <Drawer
              open={isChatHistoryOpen}
              sx={{
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  width: 300,
                  top: 0,
                  height: "100%",
                  boxSizing: "border-box",
                  [theme.breakpoints.up("md")]: {
                    top: 66,
                    height: "calc(100% - 66px)",
                  },
                },
              }}
              variant={!isLg ? undefined : "persistent"}
              onClose={() => setChatHistory(false)}
              anchor="right"
            >
              <ChatHistoryPanel />
            </Drawer>
          </ClickAwayListener>
        )}
      </div>
    </FilesDropzone>
  );
}
